import Phaser from 'phaser';

/**
 * VirtualControls: Touch UI Overlay for Android, Tablet & Mobile Screens
 * Multi-touch responsive touch control system for high-intensity 60 FPS platforming.
 * Directly binds in screen-space (scrollFactor = 0) with multi-pointer tracking.
 */
export class VirtualControls {
  constructor(scene) {
    this.scene = scene;
    this.leftPressed = false;
    this.rightPressed = false;
    this.slidePressed = false;
    this.antiGravityCallback = null;

    this.createControls();
    this.setupMultiTouchTracking();
  }

  setAntiGravityCallback(cb) {
    this.antiGravityCallback = cb;
  }

  get scale() {
    return this.scene.scale;
  }

  createControls() {
    const { width, height } = this.scale;
    const bottomY = height - 76;
    const leftX = 52;
    const rightX = 136;
    const slideX = 220;
    const agX = width - 70;

    // 1. LEFT BUTTON (Direct screen space, depth 100)
    this.btnLeft = this.scene.add.image(leftX, bottomY, 'btn_arrow_left')
      .setScrollFactor(0)
      .setDepth(100)
      .setInteractive({ useHandCursor: true })
      .setAlpha(0.88);

    // 2. RIGHT BUTTON
    this.btnRight = this.scene.add.image(rightX, bottomY, 'btn_arrow_right')
      .setScrollFactor(0)
      .setDepth(100)
      .setInteractive({ useHandCursor: true })
      .setAlpha(0.88);

    // 3. SLIDE BUTTON (Rockstar Knee Slide)
    this.btnSlide = this.scene.add.image(slideX, bottomY, 'btn_slide')
      .setScrollFactor(0)
      .setDepth(100)
      .setInteractive({ useHandCursor: true })
      .setAlpha(0.92);

    // 4. BIG ANTI-GRAVITY BUTTON
    this.btnAG = this.scene.add.image(agX, bottomY, 'btn_antigravity')
      .setScrollFactor(0)
      .setDepth(100)
      .setInteractive({ useHandCursor: true })
      .setScale(0.92);

    // Pulsing neon breathing animation on Anti-Gravity button
    this.agTween = this.scene.tweens.add({
      targets: this.btnAG,
      scaleX: 1.0,
      scaleY: 1.0,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // Anti-Gravity Button Label
    this.agLabel = this.scene.add.text(agX, bottomY + 46, 'ANTI-GRAV', {
      fontFamily: 'Impact, Arial Black, sans-serif',
      fontSize: '12px',
      color: '#fde047',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100);

    // Keyboard support for desktop/browser testing
    if (this.scene.input && this.scene.input.keyboard) {
      this.cursors = this.scene.input.keyboard.createCursorKeys();
      this.keyA = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
      this.keyD = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
      this.keyW = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W);
      this.keyS = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S);
      this.keySpace = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

      this.keySpace.on('down', () => this.triggerAntiGravity());
      this.keyW.on('down', () => this.triggerAntiGravity());
      if (this.cursors.up) {
        this.cursors.up.on('down', () => this.triggerAntiGravity());
      }
    }

    this.scene.scale.on('resize', this.handleResize, this);
  }

  setupMultiTouchTracking() {
    // Global pointer down handler for instant tap response
    this.pointerDownHandler = (pointer) => {
      const px = pointer.x;
      const py = pointer.y;

      // Anti-Gravity check:
      // Either directly touching the AG button (radius 65) OR right-hand lower screen zone
      const distAG = Phaser.Math.Distance.Between(px, py, this.btnAG.x, this.btnAG.y);
      const isRightZone = px > (this.scale.width - 160) && py > (this.scale.height - 200);

      if (distAG < 65 || isRightZone) {
        this.triggerAntiGravity();
      }
    };

    this.scene.input.on('pointerdown', this.pointerDownHandler);
  }

  triggerAntiGravity() {
    if (this.btnAG) {
      this.btnAG.setScale(0.85);
      this.scene.time.delayedCall(120, () => {
        if (this.btnAG) this.btnAG.setScale(0.92);
      });
    }
    if (this.antiGravityCallback) {
      this.antiGravityCallback();
    }
  }

  /**
   * Continuous multi-touch evaluation every frame (called from GameScene update)
   * Tracks multiple simultaneous fingers smoothly even when sliding across the screen
   */
  update() {
    let left = false;
    let right = false;
    let slide = false;

    // Collect all active touch pointers + mouse
    const pointers = [];
    if (this.scene.input) {
      if (this.scene.input.pointer1 && this.scene.input.pointer1.isDown) pointers.push(this.scene.input.pointer1);
      if (this.scene.input.pointer2 && this.scene.input.pointer2.isDown) pointers.push(this.scene.input.pointer2);
      if (this.scene.input.pointer3 && this.scene.input.pointer3.isDown) pointers.push(this.scene.input.pointer3);
      if (this.scene.input.mousePointer && this.scene.input.mousePointer.isDown) pointers.push(this.scene.input.mousePointer);
    }

    for (const p of pointers) {
      const px = p.x;
      const py = p.y;

      // Left button zone (generous 54px touch radius)
      if (Phaser.Math.Distance.Between(px, py, this.btnLeft.x, this.btnLeft.y) < 54) {
        left = true;
      }
      // Right button zone (generous 54px touch radius)
      if (Phaser.Math.Distance.Between(px, py, this.btnRight.x, this.btnRight.y) < 54) {
        right = true;
      }
      // Slide button zone (generous 52px touch radius)
      if (Phaser.Math.Distance.Between(px, py, this.btnSlide.x, this.btnSlide.y) < 52) {
        slide = true;
      }
    }

    this.leftPressed = left;
    this.rightPressed = right;
    this.slidePressed = slide;

    // Visual button feedback
    if (this.btnLeft) {
      this.btnLeft.setScale(left ? 0.92 : 1.0).setAlpha(left ? 1.0 : 0.85);
    }
    if (this.btnRight) {
      this.btnRight.setScale(right ? 0.92 : 1.0).setAlpha(right ? 1.0 : 0.85);
    }
    if (this.btnSlide) {
      this.btnSlide.setScale(slide ? 0.92 : 1.0).setAlpha(slide ? 1.0 : 0.88);
    }
  }

  handleResize(gameSize) {
    const { width, height } = gameSize;
    const bottomY = height - 76;
    const leftX = 52;
    const rightX = 136;
    const slideX = 220;
    const agX = width - 70;

    if (this.btnLeft) this.btnLeft.setPosition(leftX, bottomY);
    if (this.btnRight) this.btnRight.setPosition(rightX, bottomY);
    if (this.btnSlide) this.btnSlide.setPosition(slideX, bottomY);
    if (this.btnAG) this.btnAG.setPosition(agX, bottomY);
    if (this.agLabel) this.agLabel.setPosition(agX, bottomY + 46);
  }

  getHorizontalInput() {
    let axis = 0;
    const leftKey = (this.cursors && this.cursors.left && this.cursors.left.isDown) || (this.keyA && this.keyA.isDown);
    const rightKey = (this.cursors && this.cursors.right && this.cursors.right.isDown) || (this.keyD && this.keyD.isDown);

    if (this.leftPressed || leftKey) {
      axis -= 1;
    }
    if (this.rightPressed || rightKey) {
      axis += 1;
    }
    return axis;
  }

  isSlideRequested() {
    const downKey = (this.cursors && this.cursors.down && this.cursors.down.isDown) || (this.keyS && this.keyS.isDown);
    return this.slidePressed || downKey;
  }

  destroy() {
    if (this.scene && this.scene.scale) {
      this.scene.scale.off('resize', this.handleResize, this);
    }
    if (this.pointerDownHandler && this.scene && this.scene.input) {
      this.scene.input.off('pointerdown', this.pointerDownHandler);
    }
    if (this.agTween) {
      this.agTween.stop();
    }
    if (this.btnLeft) this.btnLeft.destroy();
    if (this.btnRight) this.btnRight.destroy();
    if (this.btnSlide) this.btnSlide.destroy();
    if (this.btnAG) this.btnAG.destroy();
    if (this.agLabel) this.agLabel.destroy();
  }
}
